import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { ArrowBackIcon, ArrowForwardIcon, Box, HStack, IconButton, useMediaQuery } from 'native-base';
import { find } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import FeatherIcons from 'react-native-vector-icons/Feather';
import moment from 'moment';

import { Button, LightBulbIcon, Modal, PrinterIcon, SwitchButton, Text } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { CleaningScheduleTypeEnum, CleaningTaskAssignmentDto } from '@pimm/services/lib/sms-workforce';
import { formatTo, stringToDateLocal } from '@app/utils/date-formatter';
import { MainScreenProps } from '@app/navigations/root';
import { MainContainer } from '@app/components/core';
import { ScreenLayout } from '@app/components/layout';
import { DateTimePopover, useSiteConfig, useSiteTime } from '@app/features/store-core';
import { ModuleTitle } from '@app/features/app';
import { ModalAppDocuments } from '@app/features/store-info/components';
import {
  CleaningAssignmentsProvider,
  CleaningForm,
  GetCleaningPlanParams,
  useGetEmployees,
  useGetCleaningPlan,
  useUpdateCleaningTask,
} from '@app/features/cleaning-tasks';
import { getScheduleColor } from '@app/features/cleaning-tasks/utils';
import { PrintCleaningPlan } from '@app/features/cleaning-tasks/components/print-cleaning-plan';
import { ResourceLoader } from '@app/components/shared';
import { useGetClientId } from '@app/features/app/hooks';
import { SignalRContext, SignalRData, SignalREffectKey } from '@app/features/signalr/context';

export type CleaningTasksScreenProps = MainScreenProps<'CleaningTasks'>;

export const CleaningTasksScreen: React.FC<CleaningTasksScreenProps> = ({ route: { params, ...route } }) => {
  const [isSmallScreen] = useMediaQuery({ maxWidth: 1200 });
  const { locale } = useAppLocale();
  const { t } = useTranslation();
  const { siteConfig } = useSiteConfig();
  const siteTime = useSiteTime();
  const queryClient = useQueryClient();
  const xClientId = useGetClientId();
  const [cleaningPlan, setCleaningPlanParams] = useGetCleaningPlan();
  const [employees, setEmployeesParams] = useGetEmployees();
  const [week, setWeek] = useState<ReturnType<typeof siteTime.toStartEndOfWeek> | undefined>(undefined);

  const updateCleaningTask = useUpdateCleaningTask();
  const [selectedScheduleType, setSelectedScheduleType] = useState<CleaningScheduleTypeEnum>(CleaningScheduleTypeEnum.Daily);
  const [editingSelectedDate, setEditingSelectedDate] = useState<string | undefined>(undefined);
  const isEditMode = useBoolean();
  const isPrintOpen = useBoolean();
  // Quick global filter: when on, hide tasks whose status is Complete or
  // NotApplicable across all three columns (Daily / Weekly / Monthly).
  const showOnlyPending = useBoolean();

  // Temporarily allow editing even if not within the editable window
  // as per sir tom's request to test the crossover week functionality — can re-enable the disabled state after testing
  const maxDate = moment(siteTime.toStartEndOfWeek().endDate).add(7, 'days').toDate();
  const isCurrentWeek = week && siteTime.today() > week.startDate && siteTime.today() < week.endDate;

  const handleSelectWeek = date => {
    setWeek(siteTime.toStartEndOfWeek(date));
  };

  const handleToggleIsEditable = (scheduleType: CleaningScheduleTypeEnum, selectedDate?: string) => {
    setSelectedScheduleType(scheduleType);
    setEditingSelectedDate(selectedDate);
    isEditMode.setTrue();
  };

  const handleChangeWeek = (days?: number) => () => {
    if (week) {
      let startOfWeek = siteTime.toStartEndOfWeek().startDate;
      if (days) {
        startOfWeek = moment(week.startDate).add(days, 'days').toDate();
      }
      setWeek(siteTime.toStartEndOfWeek(startOfWeek));
    }
  };

  useEffect(() => {
    if (week?.startDate && siteConfig?.id) {
      setCleaningPlanParams({ date: week.startDate, siteId: siteConfig.id });
      setEmployeesParams({ siteId: siteConfig.id });
    }
  }, [week, siteConfig?.id]);

  useEffect(() => {
    if (siteConfig?.id && !week?.startDate) {
      const startEndOfBlock = siteTime.toStartEndOfBlock();
      const startEndOfWeek = siteTime.toStartEndOfWeek(startEndOfBlock.startTime);
      setWeek(startEndOfWeek);
    }
  }, [siteConfig?.id, week]);

  SignalRContext.useSignalREffect(
    SignalREffectKey,
    jsonString => {
      const message: SignalRData<CleaningTaskAssignmentDto | CleaningTaskAssignmentDto[], undefined> = JSON.parse(jsonString);

      if (message.moduleName === 'CleaningTask') {
        if (!xClientId || message.data.clientId === xClientId) return;

        const isDeleted = (message.data as typeof message.data & { isDeleted?: boolean }).isDeleted;
        const rawData = message.data.data;
        const dataArray = Array.isArray(rawData) ? rawData : [rawData];
        const queryCache = queryClient.getQueryCache();

        for (const _data of dataArray) {
          const cachedGetCleaningPlan = queryCache.getAll().find(cache => {
            const params = cache.queryKey[1] as GetCleaningPlanParams;
            if (cache.queryKey.includes('GetCleaningPlan') && params.siteId === message.siteId) {
              const primaryDate = isDeleted ? _data?.statusDate ?? _data?.dueDate : _data?.dueDate;
              const selectedDate = primaryDate ? stringToDateLocal(primaryDate) : null;
              if (!selectedDate || !week?.startDate || !week?.endDate) return false;

              const isWithinWeek =
                (_data?.scheduleType === CleaningScheduleTypeEnum.Daily || _data?.scheduleType === CleaningScheduleTypeEnum.Weekly) &&
                selectedDate >= week.startDate &&
                selectedDate <= week.endDate;

              const isWithinMonth =
                _data?.scheduleType === CleaningScheduleTypeEnum.Monthly &&
                moment(selectedDate).format('MMM YYYY') === moment(week.startDate).format('MMM YYYY');

              return isWithinWeek || isWithinMonth;
            }
            return false;
          });

          if (cachedGetCleaningPlan?.queryKey[1]) {
            if (isDeleted) {
              updateCleaningTask(cachedGetCleaningPlan.queryKey[1] as GetCleaningPlanParams, _data, true);
            } else {
              let _taskAssignment = _data;

              // Making sure to include employee name
              if (_data.employeeId && !_data.firstName) {
                const employee = find(employees?.data, ['id', _data.employeeId]);

                if (employee) {
                  _taskAssignment = { ..._data, firstName: employee?.firstName, lastName: employee?.lastName };
                } else {
                  _taskAssignment = { ..._data, employeeId: undefined };
                }
              }

              updateCleaningTask(cachedGetCleaningPlan.queryKey[1] as GetCleaningPlanParams, _taskAssignment);
            }
          }
        }
      }
    },
    [],
  );

  return (
    <MainContainer>
      <ScreenLayout
        title={
          <HStack flex={1} alignItems="center">
            <ModuleTitle name={route.name} defaultText="Cleaning Tasks" />
            <ModalAppDocuments
              identifierName="Module.CleaningTasks"
              name={route.name}
              moduleType="CleaningTasks"
              trigger={triggerProps => {
                if (!triggerProps.documents?.length) return undefined;
                return (
                  <IconButton
                    rounded="lg"
                    borderWidth={1}
                    borderColor="gray.300"
                    ml={2}
                    p={0}
                    w={9}
                    h={9}
                    icon={<LightBulbIcon size="md" />}
                    onPress={triggerProps.onPress}
                  />
                );
              }}
            />
          </HStack>
        }
        actionTool={
          <HStack space={2} alignItems="center" justifyContent="flex-end">
            <HStack space={2} alignItems="center" mr={2}>
              <Text size="md" fontWeight={600} color="gray.700">
                {t('cleaning_task:pending_only')}
              </Text>
              <SwitchButton isChecked={showOnlyPending.value} onChange={() => showOnlyPending.toggle()} />
            </HStack>
            <Button
              variant="outlined"
              borderColor="gray.300"
              bg="white"
              mr={1}
              startIcon={<PrinterIcon color="gray.700" size={5} />}
              _text={{ color: 'gray.900', fontWeight: 600 }}
              onPress={isPrintOpen.setTrue}
              accessibilityLabel="Print weekly cleaning plan"
            >
              {t('cleaning_task:print_report')}
            </Button>
            <Button variant="unstyled" w={10} onPress={handleChangeWeek(-7)}>
              <ArrowBackIcon size={4} color="gray.700" />
            </Button>
            <DateTimePopover
              key={`date-picker-${week?.startDate.getTime()}`}
              selectedDate={week?.startDate}
              mode="week"
              onChange={handleSelectWeek}
              max={moment(siteTime.toStartEndOfWeek().endDate).add(1, 'week').toDate()} // NOTE: +1, User should be able to load the next week in future from current week
            >
              <Button key={locale} variant="unstyled" startIcon={<FeatherIcons name="calendar" size={18} color="gray.700" />}>
                {week ? `Wk ${week.weekNumber} (${formatTo(week.startDate, 'MMM DD')} - ${formatTo(week.endDate, 'MMM DD')})` : ''}
              </Button>
            </DateTimePopover>

            <Button
              variant="unstyled"
              w={10}
              onPress={handleChangeWeek(7)}
              // Temporarily allow editing even if not within the editable window
              // as per sir tom's request to test the crossover week functionality — can re-enable the disabled state after testing
              // disabled={!week?.endDate || maxDate <= week.endDate}
            >
              <ArrowForwardIcon size={4} color="gray.700" />
            </Button>

            <Button variant="unstyled" onPress={handleChangeWeek()} mx={2} isDisabled={isCurrentWeek}>
              {t('app:current_week')}
            </Button>
            {isEditMode.value && (
              <Button
                variant="unstyled"
                background={`${getScheduleColor(selectedScheduleType)}.500`}
                borderWidth={0}
                onPress={isEditMode.setFalse}
                _text={{ color: 'white', fontSize: 'md', fontWeight: 600 }}
              >
                {t('app:exit_edit_mode')}
              </Button>
            )}
          </HStack>
        }
      >
        {!!siteConfig.id && !!week && !!week?.startDate && (
          <CleaningAssignmentsProvider cleaningPlan={cleaningPlan} siteId={siteConfig.id} employees={employees} week={week}>
            <ResourceLoader h="full" isLoading={cleaningPlan.isIdle || cleaningPlan.isLoading}>
              {isEditMode.value ? (
                <Box key={`${week.startDate.toISOString()}-edit`} h="full">
                  <CleaningForm
                    scheduleType={selectedScheduleType}
                    isEditable
                    initialSelectedDate={editingSelectedDate}
                    onToggleIsEditable={handleToggleIsEditable}
                    onChangeScheduleType={setSelectedScheduleType}
                    showOnlyPending={showOnlyPending.value}
                  />
                </Box>
              ) : isSmallScreen ? (
                <Box key={`${week.startDate.toISOString()}-small`} h="full">
                  <CleaningForm
                    scheduleType={selectedScheduleType}
                    isEditable={false}
                    onToggleIsEditable={handleToggleIsEditable}
                    onChangeScheduleType={setSelectedScheduleType}
                    showOnlyPending={showOnlyPending.value}
                  />
                </Box>
              ) : (
                <Box h="full" bg="gray.50" p={3.5} overflow="hidden">
                  <HStack key={`${week.startDate.toISOString()}`} h="full" space={3}>
                    {[
                      CleaningScheduleTypeEnum.Daily,
                      CleaningScheduleTypeEnum.Weekly,
                      ...(cleaningPlan.data?.some(
                        p =>
                          p.scheduleType === 'Monthly' &&
                          (p.categories ?? []).some(c => (c.schedules ?? []).some(s => (s.cleaningTasks ?? []).length > 0)),
                      )
                        ? [CleaningScheduleTypeEnum.Monthly]
                        : []),
                    ].map(_schedule => (
                      <Box key={_schedule} flex={1} h="full" overflow="hidden">
                        <CleaningForm
                          scheduleType={_schedule}
                          isEditable={false}
                          onToggleIsEditable={handleToggleIsEditable}
                          showOnlyPending={showOnlyPending.value}
                        />
                      </Box>
                    ))}
                  </HStack>
                </Box>
              )}
            </ResourceLoader>
          </CleaningAssignmentsProvider>
        )}
      </ScreenLayout>

      <Modal isOpen={isPrintOpen.value} size="full" hideClose noPadding onClose={isPrintOpen.setFalse} _content={{ overflow: 'hidden' }}>
        <PrintCleaningPlan cleaningPlan={cleaningPlan.data ?? []} week={week} onClose={isPrintOpen.setFalse} />
      </Modal>
    </MainContainer>
  );
};
